import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMoneyBillWave, 
  faChartLine, 
  faBox, 
  faUsers, 
  faCalendarAlt,
  faArrowLeft,
  faDownload
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { shipmentService } from '@/services/shipmentService';
import { getSubscriptionStats } from '@/services/subscriptionService';

const RevenueDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [shipmentStats, setShipmentStats] = useState(null);
  const [subscriptionStats, setSubscriptionStats] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const [shipStatsResult, subStatsResult] = await Promise.allSettled([
          shipmentService.getStats(token),
          getSubscriptionStats()
        ]);

        if (shipStatsResult.status === 'fulfilled') {
          setShipmentStats(shipStatsResult.value);
        } else {
          console.error('Shipment stats error:', shipStatsResult.reason);
          // Don't set global error yet, we might have partial data
        }

        if (subStatsResult.status === 'fulfilled') {
          setSubscriptionStats(subStatsResult.value);
        } else {
          console.error('Subscription stats error:', subStatsResult.reason);
        }

        // Only set error if all mandatory sources failed
        if (shipStatsResult.status === 'rejected' && subStatsResult.status === 'rejected') {
          setError(`Failed to load revenue data: ${shipStatsResult.reason?.message || 'Unknown error'}`);
        }
      } catch (err) {
        console.error('Revenue dashboard fetch error:', err);
        setError('An unexpected error occurred while loading revenue data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  const displayTotal = (shipmentStats?.totalRevenue || 0) + (subscriptionStats?.totalRevenue || 0);
  const displayToday = (shipmentStats?.todayDeliveryAmount || 0) + (subscriptionStats?.todayRevenue || 0);
  const totalPotential = displayTotal + (shipmentStats?.pendingRevenue || 0) + (subscriptionStats?.pendingRevenue || 0);

  return (
    <div className="p-4 md:p-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <Link to="/dashboard/admin" className="text-red-600 hover:text-red-700 flex items-center mb-2">
              <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
              Back to Dashboard
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">Revenue Dashboard</h1>
            <p className="text-gray-600">Overview of your business earnings</p>
          </div>
          <button 
            onClick={() => window.print()}
            className="mt-4 md:mt-0 flex items-center justify-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
          >
            <FontAwesomeIcon icon={faDownload} className="mr-2" />
            Download Report
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6">
            {error}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-green-100 text-green-600 rounded-lg">
                <FontAwesomeIcon icon={faMoneyBillWave} size="lg" />
              </div>
            </div>
            <h3 className="text-gray-500 text-sm font-medium">Total Revenue (Paid)</h3>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(displayTotal)}</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-blue-100 text-blue-600 rounded-lg">
                <FontAwesomeIcon icon={faCalendarAlt} size="lg" />
              </div>
            </div>
            <h3 className="text-gray-500 text-sm font-medium">Today's Sales</h3>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(displayToday)}</p>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="p-3 bg-purple-100 text-purple-600 rounded-lg">
                <FontAwesomeIcon icon={faUsers} size="lg" />
              </div>
            </div>
            <h3 className="text-gray-500 text-sm font-medium">Subscription Revenue</h3>
            <p className="text-2xl font-bold text-gray-900">{formatCurrency(subscriptionStats?.totalRevenue)}</p>
          </div>
        </div>

        {/* Detailed Breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Shipment Revenue Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100 bg-gray-50">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <FontAwesomeIcon icon={faBox} className="mr-2 text-purple-600" />
                Shipment Revenue
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-gray-50">
                  <span className="text-gray-600">Total Paid Shipments</span>
                  <span className="font-semibold text-gray-900">{shipmentStats?.totalShipments || 0}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-50">
                  <span className="text-gray-600">Delivered Revenue</span>
                  <span className="font-semibold text-green-600">{formatCurrency(shipmentStats?.totalRevenue)}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">Today's Shipment Revenue</span>
                  <span className="font-semibold text-blue-600">{formatCurrency(shipmentStats?.todayRevenue)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Subscription Revenue Details */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100 bg-gray-50">
              <h2 className="text-xl font-bold text-gray-900 flex items-center">
                <FontAwesomeIcon icon={faUsers} className="mr-2 text-orange-600" />
                Subscription Revenue
              </h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div className="flex justify-between items-center py-2 border-b border-gray-50">
                  <span className="text-gray-600">Active Subscriptions</span>
                  <span className="font-semibold text-gray-900">{subscriptionStats?.totalActive || 0}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-50">
                  <span className="text-gray-600">Total Sub. Revenue</span>
                  <span className="font-semibold text-green-600">{formatCurrency(subscriptionStats?.totalRevenue)}</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">Today's Sub. Revenue</span>
                  <span className="font-semibold text-blue-600">{formatCurrency(subscriptionStats?.todayRevenue)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default RevenueDashboard;
